import type {ArrayAddButton} from "./Array";
import type {Column} from "../../agenda/Columns";
import type {EndpointParam} from "./Array";
import type {AgendaDescriptor, AgendaMeta} from "@t/agenda/Agenda";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import {View} from "@t/agenda/View.ts";
import {FormLayout} from "@t/fd/layout/FormLayout.ts";
import {RootLayout} from "@t/fd/InterfaceRoot.ts";


/**
 * We need three basic modes of operation for grid-like items in the CRM:
 * 1. Top-level agenda - lets you browse root items from the database (e.g. buscases or opportunities).
 *    New items are creating by forwarding to a stand-alone page or modal dialog.
 * 2. Inline items - displays child items of a record. All items are part of owning data object (e.g. invoice items or offer items).
 *    New items are created directly in the form (expandable row)
 * 3. Remote items - this is a combination of the previous two. Items are displayed inside the form but agenda endpoint is used to load items.
 *   New items are creating by forwarding to a stand-alone page or modal dialog.
 **/

/**
 * Properties which are share by both agenda and items
 */
export interface DataGrid extends FormLayout {
	// Common super interface for agenda and items
	type: "items" | "agenda";

	// user can select rows for batch operations
	selectable?: boolean;
	// maximum number of rows that can be selected
	selectMax?: number;
	// selection state (boolean) is saved directly to this item property (if missing, selected rows are kept internally without data modification)
	selectionProperty?: string;

	/**
	 * How to obtain unique row identity from the row object.
	 * This is required to allow selectable rows.
	 **/
	identity?: DataGetter;

	/** If the items grid should have a header, provide this component */
	header?: FormPartOrReference;

	add?: ArrayAddButton[];

	/** These columns are always displayed independently on the view content */
	fixedColumns?: Column[];

	/** Metaobject for the grid. If missing it will be downloaded from given URL endpoint*/
	meta?: AgendaMeta;
	// Endpoint URL. The endpoint must support META method if meta property is missing
	url?: string;
	params?: EndpointParam[];   // dynamic url params
}


/**
 * Component to display form items (e.g. invoice items)
 */
export interface ItemsGrid extends DataGrid {
	type: "items";

	// Enable manual sorting by drag&drop
	sortable?: boolean;
	// ordering state (integer) is saved directly to this item property (only for manual sorting)
	orderProperty?: string;

	/** Form to edit items */
	item?: FormPartOrReference;
}


/**
 * Top-level component to display server backed agenda
 */
export interface AgendaDefinition extends DataGrid, RootLayout {
	type: "agenda";
	// The endpoint must support GET method with a standard paging interface and optionally QUERY method
	url: string;
	// if true data will be loaded only if the agenda is visible on the screen
	lazyLoad?: boolean;
}

/** Use agenda part name as the first argument of the event */
export const AGENDA_DIRTY: string = "agenda_dirty";

export function isAgenda(grid:DataGrid):grid is AgendaDefinition {
	return grid?.type === "agenda";
}


export function isItems(grid:DataGrid):grid is ItemsGrid {
	return grid?.type === "items";
}

/**
 * DataGetterRule is for obtaining value from JSON.
 * StaticData is for passing a direct value to formatters
 */
export interface DataGetter {
	d?: string;
	value?: any;
}
